import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Calculator, Zap, ArrowRight, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export default function QuoteEstimator() {
  const [config, setConfig] = useState(null);
  const [service, setService] = useState("");
  const [crop, setCrop] = useState("");
  const [acres, setAcres] = useState(40);
  const [rush, setRush] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/pricing").then(({ data }) => {
      setConfig(data);
      if (data.services?.[0]) setService(data.services[0].key);
      if (data.crops?.[0]) setCrop(data.crops[0].key);
    }).catch(() => setErr("Couldn't load pricing"));
  }, []);

  useEffect(() => {
    if (!service || !crop || !acres || acres <= 0) {
      setQuote(null);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await api.post("/pricing/estimate", {
          service_key: service,
          crop_key: crop,
          acres: Number(acres),
          rush,
        });
        setQuote(data);
      } catch (e) {
        setErr("Could not estimate");
        setQuote(null);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [service, crop, acres, rush]);

  if (!config) return null;

  return (
    <section
      id="estimate"
      data-testid="estimator-section"
      className="relative py-20 md:py-28 bg-background"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-primary to-secondary p-6 md:p-10 lg:p-14 shadow-card-hover"
        >
          {/* Decorative grid */}
          <div className="absolute inset-0 bg-grid opacity-[0.07] pointer-events-none" />
          <div className="absolute -top-32 -right-32 h-72 w-72 rounded-full bg-accent/20 blur-3xl pointer-events-none" />

          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 text-primary-foreground">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3.5 py-1.5 text-xs font-semibold tracking-wide border border-white/20">
                <Calculator className="h-3.5 w-3.5" /> Instant estimator
              </div>
              <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
                Get a price <br />
                <span className="text-accent">in 10 seconds.</span>
              </h2>
              <p className="mt-4 text-base md:text-lg text-primary-foreground/80 max-w-md">
                Tell us what you need sprayed — we'll calculate a ballpark estimate so you can plan your budget
                before you even pick up the phone.
              </p>
              <div className="mt-6 flex items-start gap-2 text-xs text-primary-foreground/65 max-w-md">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>Estimate is non-binding. Final pricing depends on access, water source, chemical supply and weather window.</span>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-2xl bg-card border border-border p-6 md:p-7 shadow-card">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Service</Label>
                    <Select value={service} onValueChange={setService}>
                      <SelectTrigger data-testid="est-service" className="mt-2 h-12 rounded-xl bg-background">
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent>
                        {config.services.map((s) => (
                          <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Crop</Label>
                    <Select value={crop} onValueChange={setCrop}>
                      <SelectTrigger data-testid="est-crop" className="mt-2 h-12 rounded-xl bg-background">
                        <SelectValue placeholder="Select crop" />
                      </SelectTrigger>
                      <SelectContent>
                        {config.crops.map((c) => (
                          <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="acres" className="text-xs font-semibold uppercase tracking-wider text-primary/70">Acres</Label>
                    <Input
                      id="acres"
                      data-testid="est-acres"
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={acres}
                      onChange={(e) => setAcres(e.target.value)}
                      className="mt-2 h-12 rounded-xl bg-background"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Same-day rush?</Label>
                    <div className="mt-2 flex items-center gap-3 h-12 px-4 rounded-xl bg-background border border-input">
                      <Switch
                        checked={rush}
                        onCheckedChange={setRush}
                        data-testid="est-rush"
                      />
                      <span className="text-sm text-foreground/70">
                        {rush ? <><Zap className="inline h-3.5 w-3.5 text-accent mr-1" />Rush scheduling</> : "Standard schedule"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Result */}
                <div className="mt-6 rounded-xl bg-muted/60 border border-border p-5" data-testid="est-result">
                  {loading ? (
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" /> Calculating…
                    </div>
                  ) : err ? (
                    <div className="text-sm text-destructive">{err}</div>
                  ) : quote ? (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground">Estimated total</div>
                      <div className="mt-1 flex items-baseline gap-3 flex-wrap">
                        <span data-testid="est-total" className="font-display text-4xl md:text-5xl font-bold text-primary">
                          {fmt(quote.total_estimate)}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          range {fmt(quote.low_estimate)} – {fmt(quote.high_estimate)}
                        </span>
                      </div>
                      <div className="mt-3 text-xs text-muted-foreground">{quote.breakdown_note}</div>
                      <div className="mt-5 flex flex-col sm:flex-row gap-3">
                        <Button asChild data-testid="est-quote-btn" className="rounded-full bg-primary hover:bg-secondary text-primary-foreground font-semibold h-11 px-6">
                          <a href="#contact">Lock in this estimate <ArrowRight className="h-4 w-4 ml-1.5" /></a>
                        </Button>
                        <div className="text-xs text-muted-foreground self-center">A real pilot will confirm pricing within 24 hours.</div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">Select a service, crop, and acreage to see your estimate.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
